import { useEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { FormField } from "components/form/FormField/FormField";
import { Button } from "components/ui/Button";
import { Dialog, DialogFooter } from "components/ui/Dialog";
import { Select } from "components/ui/Select/Select";
import { toAdministrationMethodCode } from "features/immunizations/constants/administrationMethods";
import templateStyles from "features/templates/shared/templateModal.module.css";
import { useSettingsVaccineCodes } from "features/vaccineSettings/hooks/useSettingsVaccineCodes";
import {
  ADMINISTRATION_METHOD_OPTIONS,
  VACCINE_MODAL_DEFAULT_VALUES,
  vaccineModalSchema,
} from "features/vaccineSettings/vaccineModalSchema";
import { onInvalidSubmit } from "utils/reportFormErrors";

import styles from "./VaccineModal.module.css";

const buildDefaultValues = (vaccine) => {
  if (!vaccine) {
    return VACCINE_MODAL_DEFAULT_VALUES;
  }

  return {
    name: vaccine.name || "",
    type: vaccine.type || "",
    manufacturer: vaccine.manufacturer || "",
    // Translated on the way in, so a row written before the February 2026 switch shows
    // its route instead of an empty field, and saving stores the code rather than
    // writing the display label back (KIN-1680).
    administrationMethod: toAdministrationMethodCode(vaccine.administrationMethod),
    route: vaccine.route || "",
    doseMl: vaccine.doseMl != null ? String(vaccine.doseMl) : "",
    startAge: vaccine.startAge != null ? String(vaccine.startAge) : "",
    endAge: vaccine.endAge != null ? String(vaccine.endAge) : "",
    webUrl: vaccine.webUrl || "",
    documentLink: vaccine.documentLink || "",
    vaccineCodeId: vaccine.vaccineCodeId != null ? String(vaccine.vaccineCodeId) : "",
    vaccineCodeName: vaccine.vaccineCodeName || "",
    vaccineCodeCvxCode: vaccine.vaccineCodeCvxCode || "",
    vaccineCodeMvxCode: vaccine.vaccineCodeMvxCode || "",
  };
};

export const VaccineModal = ({ show, onHide, vaccine, onSave }) => {
  const { data: vaccineCodes = [], isLoading: isLoadingVaccineCodes } =
    useSettingsVaccineCodes();

  const activeVaccineCodes = vaccineCodes.filter(
    (code) => code.active && !code.deleted && !code.disabled
  );

  // A product withdrawn since this vaccine was linked to it is not in the list above,
  // so the picker would render its placeholder while vaccineCodeId still held the id —
  // the link reads as absent when it is not. It is offered, marked, rather than hidden:
  // the link is real and the alternative is a form that silently misdescribes itself.
  // Saving is unaffected either way, since nothing clears the id unless the field is
  // changed by hand.
  const linkedWithdrawnCode = vaccineCodes.find(
    (code) =>
      String(code.id) === String(vaccine?.vaccineCodeId) &&
      !activeVaccineCodes.some((activeCode) => activeCode.id === code.id)
  );

  const form = useForm({
    resolver: zodResolver(vaccineModalSchema),
    defaultValues: VACCINE_MODAL_DEFAULT_VALUES,
  });

  const { reset, handleSubmit, watch, setValue, control } = form;

  const watchedVaccineCodeName = watch("vaccineCodeName");
  const watchedVaccineCodeCvxCode = watch("vaccineCodeCvxCode");
  const watchedVaccineCodeMvxCode = watch("vaccineCodeMvxCode");

  useEffect(() => {
    if (show) {
      reset(buildDefaultValues(vaccine));
    }
  }, [show, vaccine, reset]);

  const handleHealthlinkVaccineChange = (selectedId) => {
    if (!selectedId) {
      setValue("vaccineCodeId", "");
      setValue("vaccineCodeName", "");
      setValue("vaccineCodeCvxCode", "");
      setValue("vaccineCodeMvxCode", "");

      return;
    }

    const selectedCode = activeVaccineCodes.find(
      (code) => String(code.id) === selectedId
    );

    if (selectedCode) {
      setValue("vaccineCodeId", String(selectedCode.id));
      setValue("vaccineCodeName", selectedCode.name);
      setValue("vaccineCodeCvxCode", selectedCode.cvxCode || "");
      setValue("vaccineCodeMvxCode", selectedCode.mvxCode || "");
    }
  };

  const handleClose = () => {
    reset(VACCINE_MODAL_DEFAULT_VALUES);
    onHide();
  };

  const onSubmit = (values) => {
    const vaccineData = {
      ...values,
      doseMl: values.doseMl ? parseInt(values.doseMl, 10) : 0,
      startAge: values.startAge ? parseInt(values.startAge, 10) : 0,
      endAge: values.endAge ? parseInt(values.endAge, 10) : 0,
      vaccineCodeId: values.vaccineCodeId ? parseInt(values.vaccineCodeId, 10) : null,
    };

    onSave(vaccineData);
  };

  const toHealthlinkOption = (code, suffix = "") => ({
    value: String(code.id),
    label: `${code.name} - ${code.vaccineType} (Code: ${code.cvxCode})${suffix}`,
  });
  const healthlinkOptions = [
    ...(linkedWithdrawnCode
      ? [toHealthlinkOption(linkedWithdrawnCode, " — withdrawn")]
      : []),
    ...activeVaccineCodes.map((code) => toHealthlinkOption(code)),
  ];

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      title={vaccine ? "Edit Vaccine" : "Add New Vaccine"}
      width={800}
    >
      <FormProvider {...form}>
        <form className={templateStyles.form} onSubmit={handleSubmit(onSubmit, onInvalidSubmit("VaccineModal"))}>
          <div className={styles.grid}>
            <div className={styles.fullWidth}>
              <div className={templateStyles.fieldGroup}>
                <label className={templateStyles.label}>Healthlink Vaccine</label>
                <Controller
                  name="vaccineCodeId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onChange={(value) => {
                        field.onChange(value);
                        handleHealthlinkVaccineChange(value);
                      }}
                      options={healthlinkOptions}
                      placeholder={
                        isLoadingVaccineCodes
                          ? "Loading Healthlink vaccines..."
                          : "Select a Healthlink vaccine (optional)"
                      }
                      disabled={isLoadingVaccineCodes}
                      fullWidth
                    />
                  )}
                />
                {!!watchedVaccineCodeName && (
                  <p className={styles.vaccineCodeHint}>
                    Selected: {watchedVaccineCodeName} (Vaccine Code:{" "}
                    {watchedVaccineCodeCvxCode}, Manufacturer Code:{" "}
                    {watchedVaccineCodeMvxCode})
                  </p>
                )}
              </div>
            </div>

            <hr className={styles.divider} />
            <p className={styles.sectionTitle}>Practice Vaccine Details</p>

            <div className={templateStyles.fieldGroup}>
              <label className={templateStyles.label}>
                Vaccine Name <span className={templateStyles.requiredMark}>*</span>
              </label>
              <FormField name="name">
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      placeholder="Enter vaccine name"
                      className={templateStyles.input}
                    />
                  )}
                />
              </FormField>
            </div>

            <div className={templateStyles.fieldGroup}>
              <label className={templateStyles.label}>
                Type <span className={templateStyles.requiredMark}>*</span>
              </label>
              <FormField name="type">
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      placeholder="Enter vaccine type"
                      className={templateStyles.input}
                    />
                  )}
                />
              </FormField>
            </div>

            <div className={templateStyles.fieldGroup}>
              <label className={templateStyles.label}>
                Manufacturer <span className={templateStyles.requiredMark}>*</span>
              </label>
              <FormField name="manufacturer">
                <Controller
                  name="manufacturer"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      placeholder="Enter manufacturer"
                      className={templateStyles.input}
                    />
                  )}
                />
              </FormField>
            </div>

            <div className={templateStyles.fieldGroup}>
              <label className={templateStyles.label}>
                Administration Method <span className={templateStyles.requiredMark}>*</span>
              </label>
              <FormField name="administrationMethod">
                <Controller
                  name="administrationMethod"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Select
                      value={field.value}
                      onChange={field.onChange}
                      options={ADMINISTRATION_METHOD_OPTIONS}
                      placeholder="Select administration method"
                      error={!!fieldState.error}
                      fullWidth
                    />
                  )}
                />
              </FormField>
            </div>

            <div className={templateStyles.fieldGroup}>
              <label className={templateStyles.label}>
                Route <span className={templateStyles.requiredMark}>*</span>
              </label>
              <FormField name="route">
                <Controller
                  name="route"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      placeholder="Enter route"
                      className={templateStyles.input}
                    />
                  )}
                />
              </FormField>
            </div>

            <div className={templateStyles.fieldGroup}>
              <label className={templateStyles.label}>Dose (ml)</label>
              <FormField name="doseMl">
                <Controller
                  name="doseMl"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="number"
                      placeholder="Enter dose in ml"
                      className={templateStyles.input}
                      min="0"
                      step="0.1"
                    />
                  )}
                />
              </FormField>
            </div>

            <div className={templateStyles.fieldGroup}>
              <label className={templateStyles.label}>Start Age (months)</label>
              <FormField name="startAge">
                <Controller
                  name="startAge"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="number"
                      placeholder="Enter start age"
                      className={templateStyles.input}
                      min="0"
                    />
                  )}
                />
              </FormField>
            </div>

            <div className={templateStyles.fieldGroup}>
              <label className={templateStyles.label}>End Age (months)</label>
              <FormField name="endAge">
                <Controller
                  name="endAge"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="number"
                      placeholder="Enter end age"
                      className={templateStyles.input}
                      min="0"
                    />
                  )}
                />
              </FormField>
            </div>

            <div className={styles.fullWidth}>
              <div className={templateStyles.fieldGroup}>
                <label className={templateStyles.label}>Web URL</label>
                <FormField name="webUrl">
                  <Controller
                    name="webUrl"
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="text"
                        placeholder="Enter web URL"
                        className={templateStyles.input}
                      />
                    )}
                  />
                </FormField>
              </div>
            </div>

            <div className={styles.fullWidth}>
              <div className={templateStyles.fieldGroup}>
                <label className={templateStyles.label}>Document Link</label>
                <FormField name="documentLink">
                  <Controller
                    name="documentLink"
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="text"
                        placeholder="Enter document link"
                        className={templateStyles.input}
                      />
                    )}
                  />
                </FormField>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" size="medium" type="button" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" size="medium" type="submit">
              {vaccine ? "Update Vaccine" : "Add Vaccine"}
            </Button>
          </DialogFooter>
        </form>
      </FormProvider>
    </Dialog>
  );
};
